import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FileDoneOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Alert, Card, Table } from 'antd'
import DescriptionData from '@Orders/OrderDetailsPage/Status/DescriptionData'
import { SHIPMENT_LINES_PAGINATION_LIMIT } from '@Orders/logic'
import styles from '@Orders/OrderDetailsPage/styles.css'
import { shipmentLines_shipment_shipmentLines_items as ItemsType } from '@Orders/OrderDetailsPage/Queries/__generated__/shipmentLines'
import DataTableFileName from '@Orders/OrderDetailsPage/Status/DataTableFileName'
import { ColumnPropsType } from '@Orders/OrderDetailsPage/Status/types'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Image from '~/core/Image'
import Tooltip from '~/core/Tooltip'
import Text from '~/core/Text'
import colors from '~/styles/_colors.css'
import { DEFAULT_REFRESH_MSG } from '~/lib/flashMsgs'
import { clearCameraConfig } from '~/redux-modules/qmr-viewer/actions'
import {
    PartViewStateType,
    ShipmentDataTablePropType,
    ShipmentLineRecordType,
} from '../types'
import {
    useShipmentData,
    useShipmentLinesData,
    intialValues,
    PLACEMENT_PART_VIEWER,
} from '../logic'
import {
    INSPECTION_KEY,
    SUMMARY_KEY,
} from '~/apps/consumer/components/PartViewerModal/logic'
import PartViewerModal from '~/apps/consumer/components/PartViewerModal'

import QuantityData from './components/QuantityData'

const QUALITY_DOCUMENTS_TOOLTIP =
    'Includes part photos, inspection reports and certificates.'

export default function ShipmentDataTable({
    shipmentId,
    isImOrder,
    shipmentDetail,
}: ShipmentDataTablePropType) {
    const dispatch = useDispatch()

    const { loading, error, data } = useShipmentData(
        shipmentId,
        SHIPMENT_LINES_PAGINATION_LIMIT,
    )

    const [partView, setPartView] = useState<PartViewStateType>({
        isPartViewerModalVisible: false,
        ...intialValues,
    })

    const [activePage, setActivePage] = useState('')

    const toggleModalForPartViewer = (
        isPartViewerModalVisible: boolean,
        partData?: ShipmentLineRecordType,
    ) => {
        const partViewerState = {
            isPartViewerModalVisible,
            ...partData,
        } as PartViewStateType

        setPartView(partViewerState)
    }

    const renderDescriptionData = (
        processName: ItemsType['processName'],
        partData: ShipmentLineRecordType,
    ) => {
        const {
            materialName,
            processNames,
            holes,
            twoDdrawing,
            processes,
            material,
        } = partData

        const toggleModal = () => {
            toggleModalForPartViewer(true, partData)
            setActivePage(SUMMARY_KEY)
        }
        const materialPublicName = partData.material?.publicName

        return (
            <DescriptionData
                processName={processName}
                materialName={materialPublicName || materialName}
                processNames={processNames}
                processes={processes}
                material={material}
                twoDdrawing={twoDdrawing}
                holes={holes}
                toggleModal={toggleModal}
                isImOrder={isImOrder}
            />
        )
    }

    const renderQuantityData = (
        quantity: ItemsType['quantity'],
        partData: ShipmentLineRecordType,
    ) => (
        <QuantityData
            quantity={partData.quantity}
            totalQuantity={partData.totalQuantity}
        />
    )

    const onRowClick = (partData: ShipmentLineRecordType) => {
        return {
            onClick: () => {
                toggleModalForPartViewer(true, partData)
                setActivePage(SUMMARY_KEY)
            },
        }
    }

    const onClose = () => {
        toggleModalForPartViewer(false)
        dispatch(clearCameraConfig())
    }

    const renderInspectionButton = (partData: ShipmentLineRecordType) => {
        const onInspectionButtonClick = (evt: Event) => {
            evt.stopPropagation()
            toggleModalForPartViewer(true, partData)
            setActivePage(INSPECTION_KEY)
        }

        return partData.woAvailableToShow ? (
            <Button
                name="view-inspection"
                placement="shipment-data-table"
                onClick={onInspectionButtonClick}
            >
                <FileDoneOutlined />
                View quality documents and photos
            </Button>
        ) : (
            <Text color="blackAlpha45">Not available</Text>
        )
    }

    const COLUMNS: ColumnPropsType<ShipmentLineRecordType>[] = [
        {
            title: 'Name',
            key: 'key',
            fixed: 'left',
            width: '300px',
            render: (record: ShipmentLineRecordType) =>
                record && (
                    <Box>
                        {!isImOrder && (
                            <Image
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    background: 'white',
                                    marginRight: '8px',
                                }}
                                src={record?.revision?.thumbnailUri}
                            />
                        )}
                        <DataTableFileName
                            revision={record.revision}
                            twoDdrawing={record.twoDdrawing}
                            processes={record.processes}
                            isImOrder={isImOrder}
                            erpMoldId={record?.erpMoldId}
                            isOrderDetailPage={false}
                            itemsId={record.id}
                            showMoldTag={true}
                        />
                    </Box>
                ),
            align: 'left',
        },
        {
            title: 'Description',
            dataIndex: 'processName',
            key: 'processName',
            render: renderDescriptionData,
            width: '250px',
            align: 'left',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: renderQuantityData,
            width: '130px',
            align: 'center',
        },
        {
            title: (
                <Box>
                    Quality documents and photos
                    <Tooltip
                        placement="top"
                        title={
                            <Box textAlign="center">
                                {QUALITY_DOCUMENTS_TOOLTIP}
                            </Box>
                        }
                        overlayStyle={{ width: '250px' }}
                    >
                        <InfoCircleOutlined
                            style={{
                                paddingLeft: 5,
                                color: colors.link,
                            }}
                        />
                    </Tooltip>
                </Box>
            ),
            key: 'woAvailableToShow',
            render: renderInspectionButton,
            align: 'left',
        },
    ]

    const shipmentLinesData = useShipmentLinesData(data?.shipment)

    if (loading) {
        return <Card loading bordered={false} />
    }

    if (error) {
        return <Alert message={DEFAULT_REFRESH_MSG} type="error" showIcon />
    }

    return (
        <>
            <Table
                className={styles.shippingTableStyle}
                columns={COLUMNS}
                dataSource={shipmentLinesData || []}
                pagination={false}
                onRow={onRowClick}
            />
            {partView?.revision && (
                <PartViewerModal
                    partViewData={partView}
                    onClose={onClose}
                    activePage={activePage}
                    setActivePage={setActivePage}
                    isInspectionReportEnabled={true}
                    placement={PLACEMENT_PART_VIEWER}
                    isImOrder={isImOrder}
                    shipmentDetail={shipmentDetail}
                />
            )}
        </>
    )
}
